/**
 * Government Data Dashboard
 * 
 * Unified dashboard combining Integrations, Awards, and Industry News tabs.
 * Provides a single interface for managing and monitoring all government data sources.
 */

import React, { useState, useEffect } from 'react';
import { Tabs, PageHeader, PageContainer } from '@tracekit/core';
import { Database, Newspaper, Globe, TrendingUp, Briefcase } from 'lucide-react';
// TODO: Re-add Trophy icon import when Awards tab is re-enabled
import { ExternalDataProvider, useExternalData } from '../contexts/ExternalDataContext';
import { IntegrationsTab } from '../components/ExternalData/IntegrationsTab';
// TODO: Re-enable AwardsTab import when Awards feature is complete
// import { AwardsTab } from '../components/ExternalData/AwardsTab';
import { NewsTab } from '../components/ExternalData/NewsTab';
import { OpportunitiesTab } from '../components/ExternalData/OpportunitiesTab';
import { getZoneManager, Zone, ZONE_DEFINITIONS } from '../services/zoneManager';
import { Card, Button, Badge } from '@tracekit/core';

// Internal component that uses the context
function GovernmentDataDashboardContent() {
  const { selectedZone, setSelectedZone } = useExternalData();
  const zoneManager = getZoneManager();
  
  // Get tab from URL query params
  const urlParams = new URLSearchParams(window.location.search);
  const defaultTab = urlParams.get('tab') || 'opportunities';
  
  // Update URL when tab changes
  const handleTabChange = (tabId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabId);
    window.history.pushState({}, '', url.toString());
  };
  
  // Update zone when zone manager changes
  useEffect(() => {
    const listener = (newZone: Zone) => {
      setSelectedZone(newZone);
    };
    zoneManager.addListener(listener);
    return () => zoneManager.removeListener(listener);
  }, [zoneManager, setSelectedZone]);
  
  return (
    <PageContainer>
      <PageHeader
        icon={<Globe />}
        title="Government Data Center"
        subtitle="Monitor and manage government data integrations for RFP intelligence"
        actions={
          <div className="flex gap-2">
            {Object.values(ZONE_DEFINITIONS).map(zone => (
              <Button
                key={zone.id}
                variant={selectedZone === zone.id ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setSelectedZone(zone.id);
                  zoneManager.switchZone(zone.id);
                }}
                className="flex items-center gap-2"
              >
                <span className="text-lg">{zone.icon}</span>
                <span>{zone.name}</span>
                <Badge variant="primary" className="ml-1">
                  {zone.dataSources.length}
                </Badge>
              </Button>
            ))}
          </div>
        }
      />
      
      {/* Tabs */}
      <Tabs
        tabs={[
          {
            id: 'opportunities',
            label: 'Opportunities',
            icon: <Briefcase className="h-4 w-4" />,
            content: <OpportunitiesTab />,
          },
          {
            id: 'news',
            label: 'Industry News',
            icon: <Newspaper className="h-4 w-4" />,
            content: <NewsTab />,
          },
          {
            id: 'integrations',
            label: 'Data Integrations',
            icon: <Database className="h-4 w-4" />,
            content: <IntegrationsTab />,
          },
          // TODO: Re-enable Awards tab when feature is complete
          // {
          //   id: 'awards',
          //   label: 'Awards',
          //   icon: <Trophy className="h-4 w-4" />,
          //   content: <AwardsTab />,
          // },
        ]}
        defaultTab={defaultTab}
        onChange={handleTabChange}
      />
    </PageContainer>
  );
}

// Main export with context provider
export function GovernmentDataDashboard() {
  return (
    <ExternalDataProvider>
      <GovernmentDataDashboardContent />
    </ExternalDataProvider>
  );
}

